<template>
  <div class="materialRecycleBin">
    <van-notice-bar
      class="recycleNotice"
      wrapable
      :scrollable="false"
      left-icon="info-o"
      color="#ed6a0c"
      background="#fff7e8"
    >
      {{ $t('append_field.recycle_switch_off_notice') }}
    </van-notice-bar>
    <div class="topSwitchRow">
      <van-cell center :title="$t('append_field.material_recycle_bin')">
        <template #right-icon>
          <van-switch v-model="recycleBinSwitch" size="24" @change="setRecycleBinSwitch" />
        </template>
      </van-cell>
    </div>
    <div class="toolbar">
      <van-search
        v-model="fileName"
        shape="round"
        :placeholder="$t('append_field.recycle_search_placeholder')"
        @search="reload"
        @clear="reload"
      />
      <div class="typeDropdown" @click="showFileTypeSheet = true">
        <div class="typeMenuTrigger">
          <span class="typeMenuText">{{ fileTypeLabel }}</span>
          <van-icon name="arrow-down" class="typeMenuIcon" />
        </div>
      </div>
    </div>

    <div class="folderBar" @click="openFolderPicker">
      <span class="folderBarLabel">{{ $t('append_field.recycle_restore_target_folder') }}</span>
      <div class="folderBarValueWrap">
        <span class="folderBarValue">{{ restoreFolderDisplay }}</span>
        <van-icon name="arrow-down" class="folderBarIcon" />
      </div>
    </div>

    <div class="summaryBar">
      <span>{{ $t('append_field.recycle_summary_total', { n: total }) }}</span>
      <span class="sep">·</span>
      <span>{{ $t('append_field.recycle_summary_size', { s: formatTotalSizeText }) }}</span>

    </div>

    <div v-if="list.length > 0" class="selectAllBar">
      <van-checkbox v-model="checkAll" label-position="left" :indeterminate="isIndeterminate">
        {{ $t('append_field.select_all') }}
      </van-checkbox>

    </div>

    <div class="listWrap">
      <van-checkbox-group v-model="checkedIds" v-if="list.length > 0">
        <van-list
          v-model="loading"
          :finished="finished"
          :immediate-check="false"
          :finished-text="finishedText"
          @load="onLoad"
        >
          <van-cell
            v-for="item in list"
            :key="rowKey(item)"
            class="recycleCell"
            center
            @click.stop="toggleCheck(item)"
          >
            <template #icon>
              <div class="thumb">
                <img :src="thumbFor(item)" class="thumbImg" alt="" @error="onThumbErr($event)" />
              </div>
            </template>
            <template #title>
              <div class="cellMain">
                <div class="name">{{ item.name || '—' }}</div>
                <div class="meta">
                  <span class="mtSize">{{ formatBytes(item.mtSize) }}</span>
                  <template v-if="autoCleanRemainLine(item)">
                    <span class="dot">·</span>
                    <span
                      class="remainDays"
                      :class="{ warn: (recycleRemainDaysFor(item) || 0) <= 3 }"
                    >
                      {{ autoCleanRemainLine(item) }}
                    </span>
                  </template>
                </div>
              </div>
            </template>
            <template #right-icon>
              <van-checkbox :name="materialIdStr(item)" @click.stop />
            </template>
          </van-cell>
        </van-list>
      </van-checkbox-group>

      <van-empty
        v-if="!loading && list.length === 0 && finished"
        image="/static/images/terminal/empty-img.png"
        :description="$t('append_field.data_empty')"
      />
    </div>

    <div class="bottomBar">
      <div class="batchDropdown">
        <van-dropdown-menu direction="up" active-color="#006EFF">
          <van-dropdown-item v-model="batchMode" :options="batchModeOptions" />
        </van-dropdown-menu>
      </div>
      <van-button
        round
        class="batchSubmitBtn"
        :type="isDeleteMode ? 'danger' : 'primary'"
        :color="isDeleteMode ? undefined : '#006EFF'"
        @click="executeBatchAction"
      >
        {{ isDeleteMode ? $t('append_field.delete') : $t('append_field.recycle_btn_restore') }} {{  checkedIds.length > 0 ?checkedIds.length : "" }}
      </van-button>
    </div>

    <van-action-sheet
      v-model="showFileTypeSheet"
      :actions="fileTypeActions"
      :cancel-text="$t('append_field.cancel')"
      close-on-click-action
      @select="onFileTypeSelect"
    />

    <van-popup
      v-model="showFolderPicker"
      position="bottom"
      round
      class="folderPickerPopup"
      :close-on-click-overlay="true"
    >
      <div class="folderPickerHead">
        <span class="folderPickerTitle">{{ $t('append_field.recycle_folder_pick_title') }}</span>
        <van-icon name="cross" class="folderPickerClose" @click="closeFolderPicker" />
      </div>

      <div class="folderPickerScroll">
        <van-loading v-if="folderTreeLoading" vertical class="folderTreeLoading">
          {{ $t('append_field.loadinga') }}
        </van-loading>
        <template v-else>
          <van-empty
            v-if="!folderRootNodes.length"
            :description="$t('append_field.recycle_folder_empty')"
          />
          <van-checkbox-group v-else v-model="folderPickerCheckedIds" @change="onFolderPickerCheckChange">
            <van-cell
              v-for="(node, idx) in folderRootNodes"
              :key="folderRowKey(node, idx)"
              class="folderPickCell"
              center
              @click.stop="toggleFolderPickRow(node)"
            >
              <template #title>
                <div class="folderCellTitle">{{ node.name || '—' }}</div>
              </template>
              <template #right-icon>
                <van-checkbox :name="folderIdStr(node)" @click.stop />
              </template>
            </van-cell>
          </van-checkbox-group>
        </template>
      </div>
    </van-popup>
  </div>
</template>

<script>
import qs from 'qs'
import { Toast, Dialog } from 'vant'

export default {
  name: 'MaterialRecycleBin',
  data() {
    return {
      fileName: '',
      /** 筛选传参 fileType：空=全部；与列表项 typeId 一致 1 文档 / 3 图片 / 4 音频 / 5 视频 */
      fileTypeFilter: 'all',
      fileTypeOptions: [],

      list: [],
      pageNum: 1,
      pageSize: 45,
      total: 0,
      /** 接口 totalSize，按字节展示（若后端为其它单位需再换算） */
      totalSize: 0,
      loading: false,
      finished: false,
      finishedText: '',
      fetching: false,
      checkedIds: [],
      batchMode: 'restoreSelected',
      batchModeOptions: [],
      /** 类型筛选：底部动作面板 */
      showFileTypeSheet: false,

      /** 恢复目标分组（仅一级：findAllOrOneFolder 返回的 children） */
      folderRootNodes: [],
      showFolderPicker: false,
      folderTreeLoading: false,
      /** 弹层内勾选的分组 id（与 van-checkbox name 一致，单选） */
      folderPickerCheckedIds: [],
      /** 程序化回显勾选时忽略 @change，避免误触发立即关闭导致二次打开只有遮罩 */
      folderPickerSuppressChange: false,
      restoreFolderId: null,
      restoreFolderLabel: '',

      failedThumb: '/static/images/icon_loader_failed_braseas.png',
      recycleBinDays:null,
      recycleBinSwitch: false, // 与 localStorage userList.recycleBinEnabled 同步：开启 true / 关闭 false
    }
  },
  computed: {
    fileTypeApi() {
      if (this.fileTypeFilter === 'all') return ''
      return String(this.fileTypeFilter)
    },
    isDeleteMode() {
      return this.batchMode === 'clearSelected' || this.batchMode === 'clearAll'
    },
    allRowIds() {
      return this.list.map((item) => this.materialIdStr(item)).filter((id) => id !== '')
    },
    isIndeterminate() {
      const ids = this.allRowIds
      if (!ids.length) return false
      let n = 0
      ids.forEach((id) => {
        if (this.checkedIds.includes(id)) n += 1
      })
      return n > 0 && n < ids.length
    },
    checkAll: {
      get() {
        const ids = this.allRowIds
        if (!ids.length) return false
        return ids.every((id) => this.checkedIds.includes(id))
      },
      set(checked) {
        const ids = this.allRowIds
        if (!ids.length) return
        if (checked) {
          const merged = new Set([...this.checkedIds.map(String), ...ids.map(String)])
          this.checkedIds = Array.from(merged)
        } else {
          const rm = new Set(ids.map(String))
          this.checkedIds = this.checkedIds.filter((x) => !rm.has(String(x)))
        }
      },
    },
    formatTotalSizeText() {
      return this.formatBytes(this.totalSize)
    },
    fileTypeLabel() {
      const opt = this.fileTypeOptions.find((o) => o.value === this.fileTypeFilter)
      return opt ? opt.text : this.$t('append_field.recycle_type_all')
    },
    fileTypeActions() {
      return this.fileTypeOptions.map((o) => ({
        name: o.text,
        value: o.value,
      }))
    },
    restoreFolderDisplay() {
      if (this.restoreFolderLabel) return this.restoreFolderLabel
      return this.$t('append_field.recycle_folder_tap_select')
    },
  },
  created() {
    this.recycleBinDays = localStorage.getItem('recycleBinDays')
    this.syncRecycleBinSwitchFromStorage()
    this.fileTypeOptions = [
      { text: this.$t('append_field.recycle_type_all'), value: 'all' },
      { text: this.$t('append_field.document'), value: '1' },
      { text: this.$t('append_field.image'), value: '3' },
      { text: this.$t('append_field.audio'), value: '4' },
      { text: this.$t('append_field.control_video'), value: '5' },
    ]
    this.batchModeOptions = [
      { text: this.$t('append_field.recycle_restore_selected'), value: 'restoreSelected' },
      { text: this.$t('append_field.recycle_clear_selected'), value: 'clearSelected' },
      { text: this.$t('append_field.recycle_restore_all'), value: 'restoreAll' },
      { text: this.$t('append_field.recycle_clear_all'), value: 'clearAll' },
    ]
    this.loadFolderTree()
  },
  mounted() {
    const tabbar = document.querySelector('.van-tabbar')
    if (tabbar) tabbar.style.display = 'none'
    this.onLoad()
  },
  beforeDestroy() {
    const tabbar = document.querySelector('.van-tabbar')
    if (tabbar) tabbar.style.display = ''
  },
  methods: {
    /** 从 localStorage.userList 读取 recycleBinEnabled（0/1），初始化开关展示 */
    syncRecycleBinSwitchFromStorage() {
      try {
        const raw = localStorage.getItem('userList')
        if (!raw) return
        const user = JSON.parse(raw)
        const v = user && user.recycleBinEnabled
        this.recycleBinSwitch = v === 1 || v === true || v === '1'
      } catch (e) {
        /* ignore */
      }
    },
    /** 开关变更：调接口并写回 localStorage.userList.recycleBinEnabled */
    setRecycleBinSwitch(enabled) {
      const val = enabled ? 1 : 0
      this.$ajax.post('/etv/setRecycleBinSwitch.json', qs.stringify({ enabled: val }))
        .then((res) => {
          const data = res && res.data
          if (data && data.code === 0) {
            try {
              const raw = localStorage.getItem('userList')
              const user = raw ? { ...JSON.parse(raw), recycleBinEnabled: val } : { recycleBinEnabled: val }
              localStorage.setItem('userList', JSON.stringify(user))
            } catch (e) {
              /* ignore */
            }
            Toast(data.msg )
          } else {
            this.recycleBinSwitch = !enabled
            Toast((data && data.msg) )
          }
        })
        .catch((e) => {
          this.recycleBinSwitch = !enabled
          Toast((e && e.message) )
        })
    },
    onFileTypeSelect(action) {
      const v = action && action.value
      if (v === undefined || v === null) return
      this.fileTypeFilter = v
      this.reload()
    },
    resolveFolderId(node) {
      if (!node) return null
      const id = node.mfId != null ? node.mfId : node.id != null ? node.id : node.folderId
      return id != null && id !== '' ? id : null
    },
    folderIdStr(node) {
      const id = this.resolveFolderId(node)
      return id != null && id !== '' ? String(id) : ''
    },
    folderRowKey(node, idx) {
      const id = this.resolveFolderId(node)
      return id != null ? String(id) : `f-${idx}-${node && node.name}`
    },
    applyDefaultRestoreFolder() {
      if (this.restoreFolderId != null && this.restoreFolderId !== '') return
      const n0 = this.folderRootNodes[0]
      if (!n0) return
      const id = this.resolveFolderId(n0)
      if (id == null || id === '') return
      this.restoreFolderId = id
      this.restoreFolderLabel = n0.name || String(id)
    },
    async loadFolderTree() {
      this.folderTreeLoading = true
      try {
        const res = await this.$ajax.post(
          '/etv/findAllOrOneFolder.json',
          qs.stringify({
            mtType: '',
            type: 'select',
            id: '',
          }),
        )
        if (res && res.data && res.data.code === 0) {
          const d = res.data.data || {}
          if (d.lastSize != null && d.lastSize !== '') {
            localStorage.setItem('lastSize', d.lastSize)
          }
          const raw = d.children
          this.folderRootNodes = Array.isArray(raw) ? raw.map((x) => ({ ...x })) : []
          this.applyDefaultRestoreFolder()
        } else {
          Toast((res && res.data && res.data.msg) || this.$t('append_field.recycle_folder_load_fail'))
        }
      } catch (e) {
        Toast((e && e.message) || this.$t('append_field.recycle_folder_load_fail'))
      } finally {
        this.folderTreeLoading = false
      }
    },
    syncFolderPickerCheckedFromRestore() {
      this.folderPickerSuppressChange = true
      try {
        if (this.restoreFolderId != null && this.restoreFolderId !== '') {
          this.folderPickerCheckedIds = [String(this.restoreFolderId)]
        } else {
          this.folderPickerCheckedIds = []
        }
      } finally {
        this.$nextTick(() => {
          this.$nextTick(() => {
            this.folderPickerSuppressChange = false
          })
        })
      }
    },
    onFolderPickerCheckChange(names) {
      if (this.folderPickerSuppressChange) return
      if (!Array.isArray(names)) return
      if (names.length > 1) {
        const last = String(names[names.length - 1])
        this.folderPickerCheckedIds = [last]
        this.applyFolderByIdStr(last)
        return
      }
      if (names.length === 1) {
        this.applyFolderByIdStr(String(names[0]))
      }
    },
    toggleFolderPickRow(node) {
      const name = this.folderIdStr(node)
      if (!name) return
      const cur = this.folderPickerCheckedIds[0]
      if (cur === name) {
        this.folderPickerCheckedIds = []
      } else {
        this.folderPickerCheckedIds = [name]
        this.applyFolderByIdStr(name)
      }
    },
    closeFolderPicker() {
      this.showFolderPicker = false
    },
    applyFolderByIdStr(idStr) {
      if (!idStr) return
      const node = this.folderRootNodes.find((n) => String(this.resolveFolderId(n)) === idStr)
      if (!node) {
        Toast(this.$t('append_field.recycle_folder_invalid'))
        return
      }
      const id = this.resolveFolderId(node)
      this.restoreFolderId = id
      this.restoreFolderLabel = (node && node.name) || String(id)
      this.$nextTick(() => {
        this.showFolderPicker = false
      })
    },
    async openFolderPicker() {
      if (!this.folderRootNodes.length) {
        await this.loadFolderTree()
      }
      this.showFolderPicker = true
      await this.$nextTick()
      this.syncFolderPickerCheckedFromRestore()
    },
    restoreFolderIdForApi() {
      if (this.restoreFolderId != null && this.restoreFolderId !== '') {
        return this.restoreFolderId
      }
      return 1
    },
    back() {
      this.$router.back()
    },
    formatBytes(v) {
      const n = Number(v)
      if (!Number.isFinite(n) || n < 0) return '—'
      if (n < 1024) return `${Math.round(n)} B`
      if (n < 1024 * 1024) return `${(n / 1024).toFixed(2)} KB`
      if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(2)} MB`
      return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`
    },
    fileBase() {
      return localStorage.getItem('upFileUrl') || '/'
    },
    resolveMaterialId(item) {
      if (!item) return ''
      const id = item.id != null ? item.id : item.mtId != null ? item.mtId : item.materialId
      return id != null && id !== '' ? id : ''
    },
    materialIdStr(item) {
      return String(this.resolveMaterialId(item))
    },
    rowKey(item) {
      const id = this.resolveMaterialId(item)
      return id !== '' ? String(id) : `${item.name}-${item.url}`
    },
    getUrl(path) {
      if (!path) return ''
      const u = String(path).trim()
      if (!u) return ''
      if (/^https?:\/\//i.test(u)) return u
      const base = this.fileBase()
      const b = base.endsWith('/') ? base.slice(0, -1) : base
      const p = u.startsWith('/') ? u : `/${u}`
      return `${b}${p}`
    },
    /** 列表展示与缩略图均以接口 typeId 为准：1 文档 / 3 图片 / 4 音频 / 5 视频 */
    itemTypeId(item) {
      const n = Number(item && item.typeId)
      return Number.isFinite(n) ? n : NaN
    },
    /** 文档类仅用后缀区分图标，其余类型只看 typeId */
    docIconBySuffix(item) {
      let suf = String((item && item.suffix) || '').toLowerCase()
      if (suf.includes('pdf')) return '/static/images/program/pdf.png'
      if (suf.includes('ppt')) return '/static/images/program/ppt.png'
      if (suf.includes('xls')) return '/static/images/program/xlsx.png'
      if (suf.includes('txt')) return '/static/images/program/txt.png'
      if (suf.includes('doc')) return '/static/images/program/word.png'
      return '/static/images/program/word.png'
    },
    thumbFor(item) {
      const tid = this.itemTypeId(item)
      switch (tid) {
        case 3: {
          const u = item.screenshotUrl || item.url
          return u ? this.getUrl(u) : '/static/images/program/msic.png'
        }
        case 5: {
          const u = item.videoViewUrl || item.screenshotUrl || item.url
          return u ? this.getUrl(u) : '/static/images/program/msic.png'
        }
        case 4:
          return '/static/images/program/msic.png'
        case 1:
          return this.docIconBySuffix(item)
        default:
          return item.url ? this.getUrl(item.url) : '/static/images/program/msic.png'
      }
    },
    onThumbErr(e) {
      const el = e && e.target
      if (el) el.src = this.failedThumb
    },

    toggleCheck(item) {
      const id = this.materialIdStr(item)
      if (!id) return
      const i = this.checkedIds.indexOf(id)
      if (i > -1) this.checkedIds.splice(i, 1)
      else this.checkedIds.push(id)
    },
    reload() {
      this.pageNum = 1
      this.list = []
      this.total = 0
      this.totalSize = 0
      this.finished = false
      this.finishedText = ''
      this.checkedIds = []
      this.fetching = false
      this.fetchPage(true)
    },
    onLoad() {
      this.fetchPage(false)
    },
    async fetchPage(isRefresh) {
      if (this.fetching) return
      if (this.finished && !isRefresh) return

      this.fetching = true
      this.loading = true
      try {
        const res = await this.$ajax.post(
          '/etv/selectRecycleBinMaterials.json',
          qs.stringify({
            pageNum: this.pageNum,
            pageSize: this.pageSize,
            fileName: this.fileName || '',
            fileType: this.fileTypeApi,
          }),
        )
        if (res && res.data && res.data.code === 0) {
          const d = res.data.data || {}
          const raw = Array.isArray(d.materialList)
            ? d.materialList
            : Array.isArray(d.list)
              ? d.list
              : Array.isArray(d.records)
                ? d.records
                : []
          this.total = Number(d.totalNum ?? d.total ?? d.count ?? 0)
          if (d.totalSize != null && d.totalSize !== '') {
            this.totalSize = Number(d.totalSize)
          }

          const chunk = raw.map((it) => ({ ...it }))
          if (isRefresh) this.list = chunk
          else this.list = this.list.concat(chunk)

          const len = chunk.length
          const reachedTotal = this.total > 0 && this.list.length >= this.total
          const shortPage = len > 0 && len < this.pageSize
          const emptyPage = len === 0

          if (emptyPage || reachedTotal || shortPage) {
            this.finished = true
            this.finishedText = this.$t('append_field.noMore')
          } else {
            this.pageNum += 1
            this.finished = false
            this.finishedText = ''
          }
        } else {
          this.finished = true
          this.finishedText = ''
          Toast((res && res.data && res.data.msg) || this.$t('append_field.recycle_load_fail'))
        }
      } catch (e) {
        this.finished = true
        Toast((e && e.message) || this.$t('append_field.recycle_load_fail'))
      } finally {
        this.fetching = false
        this.loading = false
      }
    },
    payloadIds(arr) {
      return arr ? arr.join(',') : []
    },
    async postRestore(idsArr) {
      const res = await this.$ajax.post(
        '/etv/restoreFromRecycleBin.json',
        qs.stringify({
          ids: this.payloadIds(idsArr),
          folderId: this.restoreFolderIdForApi(),
        }),
      )
      return res && res.data
    },
    async postClear(idsArr) {
      const res = await this.$ajax.post(
        '/etv/clearRecycleBin.json',
        qs.stringify({
          ids: this.payloadIds(idsArr),
        }),
      )
      return res && res.data
    },
    selectedPayloadIds() {
      return this.checkedIds.map((id) => {
        const n = Number(id)
        return Number.isNaN(n) ? id : n
      })
    },
    executeBatchAction() {
      switch (this.batchMode) {
        case 'restoreSelected':
          return this.restoreSelected()
        case 'clearSelected':
          return this.clearSelected()
        case 'restoreAll':
          return this.restoreAll()
        case 'clearAll':
          return this.clearAll()
        default:
          return undefined
      }
    },
    async restoreSelected() {
      const ids = this.selectedPayloadIds()
      if (!ids.length) {
        Toast(this.$t('append_field.recycle_select_first'))
        return
      }
      try {
        const data = await this.postRestore(ids)
        if (data && data.code === 0) {
          Toast(data.msg )
          this.reload()
        } else {
          Toast((data && data.msg))
        }
      } catch (e) {
        Toast((e && e.message))
      }
    },
    restoreAll() {
      Dialog.confirm({
        title: this.$t('append_field.recycle_restore_all'),
        message: this.$t('append_field.recycle_restore_all_tip'),
      })
        .then(async () => {
          try {
            const data = await this.postRestore([])
            if (data && data.code === 0) {
              Toast(data.msg )
              this.reload()
            } else {
              Toast((data && data.msg) )
            }
          } catch (e) {
            Toast((e && e.message) )
          }
        })
        .catch(() => {})
    },
    clearSelected() {
      const ids = this.selectedPayloadIds()
      if (!ids.length) {
        Toast(this.$t('append_field.recycle_select_first'))
        return
      }
      Dialog.confirm({
        title: this.$t('append_field.recycle_clear_selected'),
        message: this.$t('append_field.recycle_clear_tip'),
      })
        .then(async () => {
          try {
            const data = await this.postClear(ids)
            if (data && data.code === 0) {
              Toast(data.msg )
              this.reload()
            } else {
              Toast((data && data.msg) )
            }
          } catch (e) {
            Toast((e && e.message) )
          }
        })
        .catch(() => {})
    },
    clearAll() {
      Dialog.confirm({
        title: this.$t('append_field.recycle_clear_all'),
        message: this.$t('append_field.recycle_clear_all_tip'),
      })
        .then(async () => {
          try {
            const data = await this.postClear([])
            if (data && data.code === 0) {
              Toast(data.msg)
              this.reload()
            } else {
              Toast((data && data.msg))
            }
          } catch (e) {
            Toast((e && e.message) )
          }
        })
        .catch(() => {})
    },
    toDateSafe(val) {
      if (!val && val !== 0) return null
      if (val instanceof Date) return isNaN(val.getTime()) ? null : val
      if (typeof val === 'number') {
        const d = new Date(val)
        return isNaN(d.getTime()) ? null : d
      }
      const str = String(val).trim()
      if (!str) return null
      const d1 = new Date(str)
      if (!isNaN(d1.getTime())) return d1
      const d2 = new Date(str.replace(/-/g, '/'))
      return isNaN(d2.getTime()) ? null : d2
    },
    getRecycleAutoDeleteRemainDays(delTime) {
      const days = Number(this.recycleBinDays)
      if (!days || days <= 0) return null
      const d = this.toDateSafe(delTime)
      if (!d) return null
      const now = new Date()
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
      const startOfDelDay = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
      const passed = Math.floor((startOfToday - startOfDelDay) / 86400000)
      const remain = days - (isFinite(passed) ? passed : 0)
      return remain > 0 ? remain : 0
    },

    /** 列表项删除时间字段（与后端字段对齐，按优先级兜底） */
    itemDelTime(item) {
      if (!item) return null
      const v =
        item.delTime ??
        item.deleteTime ??
        item.deletedTime ??
        item.gmtDeleted ??
        item.delDate ??
        item.recycleDelTime ??
        item.recycleTime
      return v != null && v !== '' ? v : null
    },
    recycleRemainDaysFor(item) {
      return this.getRecycleAutoDeleteRemainDays(this.itemDelTime(item))
    },
    autoCleanRemainLine(item) {
      const n = this.recycleRemainDaysFor(item)
      if (n == null) return ''
      return this.$t('append_field.recycle_remain_auto_clear', { n })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "../../public/static/css/app";

.materialRecycleBin {
  height: 100% !important;
  width: 100%;

  display: flex;
  flex-direction: column;

  .recycleNotice {
    flex-shrink: 0;

    ::v-deep .van-notice-bar__wrap {
      line-height: 1.5;
      padding: 8px 0;
    }
  }

  .topSwitchRow {
    flex-shrink: 0;
    background: #fff;
  }

  .folderBar {
    flex-shrink: 0;
    margin: 0 10px 8px;
    padding: 0 12px;
    min-height: 40px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    background: #fff;
    border-radius: 8px;
    box-sizing: border-box;

    .folderBarLabel {
      flex-shrink: 0;
      font-size: 13px;
      color: #646566;
    }

    .folderBarValueWrap {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 4px;
    }

    .folderBarValue {
      font-size: 13px;
      color: #323233;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      text-align: right;
    }

    .folderBarIcon {
      flex-shrink: 0;
      font-size: 14px;
      color: #969799;
    }
  }
}



.summaryBar {
  flex-shrink: 0;
  margin: 0 10px 8px;
  padding: 10px 12px;
  font-size: 13px;
  color: #646566;

  border-radius: 8px;
  line-height: 1.5;

  .sep {
    margin: 0 6px;
    color: #c8c9cc;
  }
}

.selectAllBar {
  flex-shrink: 0;
  padding: 0 16px 8px;
  margin: 0 10px;

  ::v-deep .van-checkbox__label {
    font-size: 14px;
    color: #111;
  }
  .van-checkbox{
    display: flex;
    justify-content: space-between;
  }
}

.toolbar {
  flex-shrink: 0;
  background: #fff;
  padding: 0 10px 8px;
  display: flex;
  align-items: center;
  gap: 8px;

  ::v-deep .van-search {
    flex: 1;
    min-width: 0;
    padding: 8px 0;
  }

  .typeDropdown {
    flex-shrink: 0;

    .typeMenuTrigger {
      height: 35px;
      padding: 0 10px 0 12px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 4px;
      background: #f7f8fa;
      border-radius: 8px;
      min-width: 72px;
      max-width: 104px;
      box-sizing: border-box;
    }

    .typeMenuText {
      font-size: 13px;
      color: #323233;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .typeMenuIcon {
      flex-shrink: 0;
      font-size: 14px;
      color: #969799;
    }
  }
}

.listWrap {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 8px 10px 120px;
}

.recycleCell {
  margin-bottom: 8px;
  border-radius: 10px;
  overflow: hidden;
  align-items: flex-start;

  ::v-deep .van-cell__title {
    flex: 1;
    min-width: 0;
  }
}

.thumb {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  overflow: hidden;
  background: #eee;
  flex-shrink: 0;
  margin-right: 10px;
  .thumbImg {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.cellMain {
  .name {
    font-size: 14px;
    font-weight: 500;
    color: #111;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 56vw;
  }

  .meta {
    margin-top: 4px;
    font-size: 12px;
    color: #969799;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    max-width: 56vw;

    .dot {
      margin: 0 4px;
    }

    .remainDays {
      color: #ff976a;
      font-weight: 500;

      &.warn {
        color: #ee0a24;
      }
    }
  }
}

.bottomBar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  display: flex;
  align-items: stretch;
  gap: 10px;
  padding: 10px 12px;
  background: #fff;
  box-sizing: border-box;

}

.batchDropdown {
  flex: 1;
  min-width: 0;
  border-radius: 999px;
  overflow: hidden;

  align-self: center;

  ::v-deep .van-dropdown-menu__bar {
    height: 35px;
    box-shadow: none;
  }

  ::v-deep .van-dropdown-menu__title {
    font-size: 13px;
    padding-left: 10px;
    padding-right: 24px;
  }

}

.batchSubmitBtn {
  flex: 0 0 108px;
  height: 32px;

}
</style>

<style lang="scss">
/* van-popup 默认挂到 body，需非 scoped */
.folderPickerPopup.van-popup {
  display: flex;
  flex-direction: column;
  max-height: 78vh;
  width: 100%;
  box-sizing: border-box;

  padding-bottom: env(safe-area-inset-bottom);
}

.folderPickerHead {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #ebedf0;
}

.folderPickerTitle {
  font-size: 16px;
  font-weight: 600;
  color: #323233;
}

.folderPickerClose {
  font-size: 20px;
  color: #969799;
  padding: 4px;
}

.folderPickerScroll {
  flex: 1;
  min-height: 120px;
  max-height: 56vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.folderTreeLoading {
  padding: 32px 0;
}

.folderPickCell.van-cell {
  align-items: center;
}

.folderPickCell .folderCellTitle {
  font-size: 15px;
  color: #323233;
  text-align: left;
}
</style>
